/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { ApolloError } from "@apollo/client";
import { ParentSize } from "@visx/responsive";
import dayjs from "dayjs";
import {
  Box,
  FlexContainer,
  MultipleSelect,
  SelectButton,
  Spinner,
  theme,
} from "@nordcloud/gnui";
import { KpiTimePeriod } from "~/generated/graphql";
import { KpiChart } from "~/components/Charts";
import { ErrorWrap } from "~/components/Utils";
import { dateFormat, getKpiType, KpiType } from "~/constants";
import { useOrgUnitKpi } from "../hooks";

const endDate = dayjs().format(dateFormat.shortDate);

const enum TIME_PERIOD {
  QUARTER = 3,
  YEAR = 12,
}

type ChartData = {
  targets: React.ComponentProps<typeof KpiChart>["targets"];
  timePoints: React.ComponentProps<typeof KpiChart>["data"];
};

type Props = {
  orgUnit?: {
    id: string | undefined;
    name: string;
  };
  kpiType: KpiType;
  data?: ChartData;
  error?: ApolloError;
  loading: boolean;
  period: KpiTimePeriod;
  setPeriod: (input: KpiTimePeriod) => void;
};

export function KpiDashboardChart({
  data,
  error,
  loading,
  period,
  setPeriod,
  kpiType,
  orgUnit,
}: Props) {
  const startDate = React.useMemo(
    () =>
      dayjs()
        .subtract(period === KpiTimePeriod.ThreeMonths ? 3 : 12, "month")
        .format(dateFormat.shortDate),
    [period]
  );

  const {
    data: additionalData,
    loading: additionalLoading,
    error: additionalError,
  } = useOrgUnitKpi({ period, kpiType, orgUnitId: orgUnit?.id });

  const kpi = getKpiType(kpiType);

  return (
    <ErrorWrap error={error || additionalError}>
      <Box boxStyle="lightGrey">
        <FlexContainer alignItems="center">
          <MultipleSelect size="small">
            <SelectButton
              name="quarter"
              value={TIME_PERIOD.QUARTER.toString()}
              labelText="3 months"
              isActive={period === KpiTimePeriod.ThreeMonths}
              data-testid="quarter"
              style={{ width: "8rem" }}
              onClick={() => setPeriod(KpiTimePeriod.ThreeMonths)}
            />
            <SelectButton
              name="year"
              value={TIME_PERIOD.YEAR.toString()}
              labelText="Up to 12 months"
              isActive={period === KpiTimePeriod.TwelveMonths}
              data-testid="year"
              style={{ width: "8rem" }}
              onClick={() => setPeriod(KpiTimePeriod.TwelveMonths)}
            />
          </MultipleSelect>
          {(loading || additionalLoading) && (
            <div css={{ marginLeft: theme.spacing.spacing02 }}>
              <Spinner />
            </div>
          )}
        </FlexContainer>
      </Box>
      <ParentSize>
        {(parent) => (
          <KpiChart
            startDate={startDate}
            endDate={endDate}
            targets={data?.targets ?? []}
            width={parent.width}
            height={250}
            data={data?.timePoints ?? []}
            name={kpi.label}
            loading={false}
            targetType={kpi?.targetType}
            additionalData={
              orgUnit?.id
                ? {
                    data: additionalData?.timePoints ?? [],
                    title: orgUnit.name,
                  }
                : undefined
            }
          />
        )}
      </ParentSize>
    </ErrorWrap>
  );
}
